import { Box, Button, Flex, Spinner, Text, Image, Tooltip } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { MdOutlineErrorOutline, MdWarningAmber, MdClear } from "react-icons/md";
import { useParams } from "react-router-dom";
import { useReactFlow } from "reactflow";
import clsx from "clsx";

import { ToastType, useShowToast } from "@/components/toast";
import { ConfigurePanelBody } from "@/design/components/configure-panel";
import { useAppDispatch, useAppSelector } from "@/reduxHooks.ts";

import { selectPanel, hidePanel } from "../..";
import { 
  useGetWFActiveUserQuery, 
  useFixWorkflowMutation,
  useLazyGetWorkflowQuery,
  useLazyGetWorkflowEventsQuery,
  useClearWorkflowEventsMutation
} from "../../api/workflow-api";
import { currentWorkflowId, workflowStatus, setworkflowStatus } from "../../redux/workflow-slice";
import { EventType, WorkflowEvent, WorkflowNodeSchema } from "../../types/workflow-types";
import { NodeType } from "../../types";
import { categoryIcons, WORKFLOW_PANELS } from "../../utils/constants";
import { ReactFlowInstanceContext } from "../flow-editor";
import { ConvertFlowtoWorkflowPayload } from "../../utils/transform-response";
import { updateNodeWithLatestData } from "../../utils/update-nodes";
import { useWorkflowActions } from "../../hooks/useWorkflowActions";
import { snakeCaseToCapital, toCaptialSnakeCase } from "@/utils/string-utils";


const formatEventDate = (timestamp: number): string => {
  try {
    // Convert milliseconds timestamp to Date object
    const date = new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const formatDuration = (seconds: number): string => {
  if (!seconds) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
  
  return parts.join(' ');
};

// Format date from timestamp
const formatFullDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0].replace(/-/g, '-') + ' ' + 
  date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

export const LogsPanelBody = () => {
  const dispatch = useAppDispatch();
  const toast = useShowToast();
  const { toObject } = useReactFlow();
  const currentWorkflowStatus = useAppSelector(workflowStatus);
  const params = useParams();
  const logsPanel = useAppSelector(selectPanel(WORKFLOW_PANELS.LogsPanel));
  const workflowId = useAppSelector(currentWorkflowId);
  const { setNodesFn } = useContext(ReactFlowInstanceContext)!;
  const { fetchAndUpdateWorkflow } = useWorkflowActions();
  const logsContainerRef = useRef<HTMLDivElement>(null);

  const [fixWorkflow] = useFixWorkflowMutation();
  const [getWorkflow] = useLazyGetWorkflowQuery();
  const [getWorkflowEvents, { data: eventsData, isLoading: isEventsLoading }] = useLazyGetWorkflowEventsQuery();
  const [clearWorkflowEvents, { isLoading: isClearingLogs }] = useClearWorkflowEventsMutation();
  const [loadingEventName, setLoadingEventName] = useState<string | null>(null);
  const [combinedEvents, setCombinedEvents] = useState<WorkflowEvent[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);

  const { data: userData, isLoading, error, refetch: refetchUserData } = useGetWFActiveUserQuery(
    { analysisId: params.analysisId! },
    { skip: !params.analysisId }
  );

  // Reset state when panel is closed
  useEffect(() => {
    if (!logsPanel?.isVisible) {
      setCombinedEvents([]);
    }
  }, [logsPanel?.isVisible]);

  // Fetch events when the panel is first opened
  useEffect(() => {
    if (logsPanel?.isVisible && workflowId && params.analysisId) {
      setInitialLoading(true);
      
      getWorkflowEvents({
        analysisId: params.analysisId,
        workflowId,
        url: null // Force a fresh fetch when panel is opened
      }).then(() => {
        setInitialLoading(false);
      }).catch(() => {
        setInitialLoading(false);
      });
    }
  }, [logsPanel?.isVisible, workflowId, params.analysisId, getWorkflowEvents]);

  // Extract pagination data from the response directly, similar to how it's done in flow-list.tsx
  const pagination = useMemo(
    () => eventsData?.response?.pagination ?? null,
    [eventsData?.response?.pagination]
  );

  // Paginate function similar to flow-list.tsx
  const paginate = useCallback(async () => {
    if (!workflowId || !params.analysisId) return;
    
    await getWorkflowEvents({
      analysisId: params.analysisId,
      workflowId,
      url: pagination && pagination.next ? pagination.next : null,
    });
  }, [getWorkflowEvents, pagination, workflowId, params.analysisId]);

  // Handle scrolling just like in flow-list.tsx
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        
        const reachedBottom = scrollHeight - scrollTop - clientHeight < 100;
        const canFetchMore = 
          (!isEventsLoading && pagination?.next) || !eventsData;
        
        const shouldFetchMore = reachedBottom && canFetchMore;
        
        if (shouldFetchMore) {
          setIsPaginating(true);
          paginate().finally(() => {
            setIsPaginating(false);
          });
        }
      }
    },
    [pagination, paginate, isEventsLoading, eventsData]
  );

  // Check on mount and after fetch if we need to load more
  useEffect(() => {
    fetchMoreOnBottomReached(logsContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  // Add scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    fetchMoreOnBottomReached(e.currentTarget);
  }, [fetchMoreOnBottomReached]);

  // Extract events and pagination data from the response
  useEffect(() => {
    if (!eventsData) return;
    
    // Extract events from the correct path in the response
    let eventsFromApi: any[] = [];
    
    try {
      // Cast to any to bypass type checking since the actual structure may vary
      const responseData = eventsData as any;
      
      // Check multiple possible structures
      if (responseData?.response?.data?.results?.events) {
        eventsFromApi = responseData.response.data.results.events;
      } 
      else if (responseData?.data?.events) {
        eventsFromApi = responseData.data.events;
      }
      else if (responseData?.response?.data?.events) {
        eventsFromApi = responseData.response.data.events;
      }
    } catch (error) {
      // Silently handle errors - UI will show no logs state
    }
    
    // Process the API events
    const processedApiEvents = Array.isArray(eventsFromApi) ? eventsFromApi.map((apiEvent: any) => {
      // Handle both snake_case (raw API) and camelCase (existing code) property names
      return {
        eventName: apiEvent.event_name || apiEvent.eventName,
        eventMessage: apiEvent.event_message || apiEvent.eventMessage,
        eventResolutionStatus: apiEvent.event_resolution_status || apiEvent.eventResolutionStatus,
        eventType: apiEvent.event_type || apiEvent.eventType,
        createdOn: apiEvent.created_on || apiEvent.createdOn
      } as WorkflowEvent;
    }) : [];
    
    // If it's a pagination request (isPaginating is true), append the new events
    // Otherwise (initial load), replace the events
    setCombinedEvents(prev => {
      if (isPaginating) {
        // Make sure we don't add duplicate events
        const currentIds = new Set(prev.map(e => e.eventName + e.createdOn));
        const newEvents = processedApiEvents.filter(e => !currentIds.has(e.eventName + e.createdOn));
        return [...prev, ...newEvents];
      } else {
        // Initial load - replace all events
        return processedApiEvents;
      }
    });
  }, [eventsData, isPaginating]);

  const events = useMemo<WorkflowEvent[]>(() => {
    return combinedEvents;
  }, [combinedEvents]);

  const hasBlockers = useMemo(() => {
    return events.some(event => event.eventType === EventType.ACTIONABLE_BLOCKER);
  }, [events]);

  const handleFixEvent = async (event: WorkflowEvent) => {
    setLoadingEventName(event.eventName);
    
    if (!workflowId || !params.analysisId) {
      toast({
        title: "Error",
        description: "Workflow ID or Analysis ID is missing",
        status: ToastType.Error,
      });
      setLoadingEventName(null);
      return;
    }

    try {
      // First get the current workflow data
      const workflowResponse = await getWorkflow({
        workflowId
      }).unwrap();

      const currentWorkflow = workflowResponse.response.data?.workflows[0];
      
      if (!currentWorkflow) {
        toast({
          title: "Error",
          description: "Could not get current workflow data",
          status: ToastType.Error,
        });
        return;
      }

      // Get the current ReactFlow instance and convert it to the proper workflow payload format
      const instance = toObject();
      const workflowData = ConvertFlowtoWorkflowPayload(instance, currentWorkflow.workflowNodes);

      // Call fix workflow with the properly structured data
      const fixResponse = await fixWorkflow({
        analysisId: params.analysisId,
        workflowId,
        workflow: workflowData
      }).unwrap();

      // Get the updated workflow data after fix
      const updatedWorkflow = fixResponse.response.data?.workflows[0];
      if (updatedWorkflow) {
        // Update nodes with the new workflow data
        setNodesFn((nds) => nds.map((node) => {
          const updatedNode = updatedWorkflow.workflowNodes.find(
            (n: WorkflowNodeSchema) => n.workflowNodeId === (node.data as NodeType).workflowNodeId
          );
          if (updatedNode) {
            return updateNodeWithLatestData(node, updatedNode);
          }
          return node;
        }));

        // Update workflow status in Redux store
        dispatch(setworkflowStatus({
          workflowRunId: updatedWorkflow.workflowRunId ?? null,
          workflowRunStatus: updatedWorkflow.workflowStatus,
          workflowNodeStatus: updatedWorkflow.workflowNodes.map(node => ({
            workflowNodeId: node.workflowNodeId!,
            uiNodeId: node.uiNodeId,
            nodeVersionId: node.nodeVersionId!,
            nodeUsageInstanceId: node.nodeUsageInstanceId!,
            nodeStatus: node.nodeStatus,
            nodeType: node.nodeType,
            message: "",
            isLocked: node.isLocked ?? false,
            isOutput: node.isOutput ?? false,
            outputName: node.outputName ?? null,
            outputState: node.outputState ?? null,
            isOutputDataAvailable: node.isOutputDataAvailable ?? false,
            isInputDataAvailable: node.isInputDataAvailable ?? false
          })),
          startTime: null,
          endTime: null,
          message: "",
          terminateWorkflowEnabled: false
        }));

        // After successful fix, trigger a refetch of active user data to get updated events
        await fetchAndUpdateWorkflow();

        // Get the latest events after the update
        const updatedUserData = await refetchUserData();
        const updatedEvents = updatedUserData?.data?.data?.results?.events || [];
        
        // Check if there are any remaining blocking events
        const hasRemainingBlockers = updatedEvents.some(
          (e: WorkflowEvent) => e.eventType === EventType.ACTIONABLE_BLOCKER
        );

        // Only close the panel if there are no more blocking events
        if (!hasRemainingBlockers) {
          dispatch(hidePanel(WORKFLOW_PANELS.LogsPanel));
        }

        toast({
          title: "Fix Applied",
          description: "The workflow has been updated successfully",
          status: ToastType.Success,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to apply fix to the workflow",
        status: ToastType.Error,
      });
    } finally {
      setLoadingEventName(null);
    }
  };

  const handleClearLogs = async () => {
    if (hasBlockers) {
      toast({
        title: "Cannot Clear Logs",
        description: "Please resolve all blocking issues before clearing logs",
        status: ToastType.Error,
      });
      return;
    }

    if (!workflowId || !params.analysisId) {
      toast({
        title: "Error",
        description: "Workflow ID or Analysis ID is missing",
        status: ToastType.Error,
      });
      return;
    }

    try {
      // Use the RTK Query mutation instead of fetch
      await clearWorkflowEvents({
        analysisId: params.analysisId,
        workflowId
      }).unwrap();
      
      // Reset pagination and events
      setIsPaginating(false);
      setCombinedEvents([]);
      
      // Refresh events after clearing
      await getWorkflowEvents({
        analysisId: params.analysisId,
        workflowId,
        url: null // Force fresh fetch
      });
      
      toast({
        title: "Logs Cleared",
        description: "All logs have been cleared successfully",
        status: ToastType.Success,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear logs",
        status: ToastType.Error,
      });
    }
  };

  // Render loading spinner for pagination
  const renderPaginationLoader = () => {
    if (!isPaginating) return null;
    
    return (
      <Flex justify="center" py={2}>
        <Spinner size="sm" />
      </Flex>
    );
  };

  if (initialLoading || isEventsLoading) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-gray-50 !w-full p-4 rounded-md gap-3 !self-center"
          align="center"
          justify="center"
          direction="column"
        >
          <Spinner size="md" className="text-gray-600 mb-2" />
          <Text className="text-gray-600">Loading workflow logs...</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  if (error) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-red-50 !w-full p-3 rounded-md gap-3 !self-center border border-red-200"
          align="center"
          justify="center"
        >
          <MdOutlineErrorOutline size={20} className="text-red-500" />
          <Text className="text-red-600">Failed to load logs</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  if (events.length === 0) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-gray-50 !w-full p-3 rounded-md gap-3 !self-center border border-gray-200"
          align="center"
          justify="center"
        >
          <MdOutlineErrorOutline size={20} className="text-gray-400" />
          <Text className="text-gray-600">No logs available</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  const renderInformationalEvent = (event: WorkflowEvent) => {
    // Handle null or undefined event
    if (!event) {
      return null;
    }
    
    // First determine if the message is already an object or a string that needs parsing
    let eventData: any;
    
    if (typeof event.eventMessage === 'string') {
      try {
        // Try to parse the string as JSON
        eventData = JSON.parse(event.eventMessage);
      } catch (e) {
        // If parsing fails, just display the raw message as a string
        return (
          <Flex direction="column" gap={3}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={2}>
                <Text className="font-medium text-gray-700">
                  {snakeCaseToCapital(event.eventName || '')}
                </Text>
              </Flex>
              <Text className="text-xs text-gray-500">
                {formatEventDate(event.createdOn || Date.now())}
              </Text>
            </Flex>
            
            <Text className="text-sm text-gray-700">
              {event.eventMessage || 'No message available'}
            </Text>
          </Flex>
        );
      }
    } else if (typeof event.eventMessage === 'object' && event.eventMessage !== null) {
      // If it's already an object, use it directly
      eventData = event.eventMessage;
    } else {
      // If it's neither a valid JSON string nor an object, just show the name
      return (
        <Flex direction="column" gap={3}>
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={2}>
              <Text className="font-medium text-gray-700">
                {snakeCaseToCapital(event.eventName || '')}
              </Text>
            </Flex>
            <Text className="text-xs text-gray-500">
              {formatEventDate(event.createdOn || Date.now())}
            </Text>
          </Flex>
          
          <Text className="text-sm text-gray-700">
            {String(event.eventMessage || 'No message available')}
          </Text>
        </Flex>
      );
    }
    
    // Now we have eventData as an object, display it in the specialized format
    const nodeDisplayName = eventData?.workflowNodeDisplayName || 'Node';
    const durationSeconds = eventData?.durationSeconds || 0;
    const totalCost = eventData?.totalCost || 0;
    const rowCount = eventData?.rowCount || 0;
    const triggeredByUserEmail = eventData?.runByEmail|| eventData?.runBy || 'Unknown';
    const triggeredByUserDisplayName = eventData?.runByUserName || triggeredByUserEmail ||'Unknown';
    const triggeredByUserID = eventData?.runByUserID || 'Unknown';

    const nodeStatus = eventData?.nodeStatus || 'Unknown';
    const nodeImageUrl = eventData?.nodeImageUrl || '';
    const nodeCategory = toCaptialSnakeCase(eventData?.nodeCategory || 'SOURCE');

    const runStartedAt = eventData?.runStartedAt || Date.now();
    // convert nodeStatus to title case
    const nodeStatusTitle = snakeCaseToCapital(nodeStatus);
    
    // Convert node display name to uppercase
    const nodeDisplayNameUpper = nodeDisplayName.toUpperCase();
    
    // Determine status color scheme based on nodeStatus
    const getStatusColorScheme = (status: string) => {
      const statusLower = status.toLowerCase();
      if (statusLower === 'success' || statusLower === 'succeeded' || statusLower === 'completed') {
        return {
          bg: 'bg-green-50',
          border: 'border-green-100',
          text: 'text-green-700'
        };
      } else if (statusLower === 'failed' || statusLower === 'error') {
        return {
          bg: 'bg-red-50',
          border: 'border-red-100',
          text: 'text-red-700'
        };
      } else if (statusLower === 'cancelled' || statusLower === 'canceled') {
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-700'
        };
      } else {
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-100',
          text: 'text-blue-700'
        };
      }
    };
    
    const statusColors = getStatusColorScheme(nodeStatus);
    
    return (
      <Flex direction="column" sx={{ padding: 0 }}>
        {/* Header with icon, title and date */}
        <Flex 
          justify="space-between" 
          align="center" 
          sx={{ paddingY: "6px" }}
          gap={4}
        >
          <Flex align="center" gap={3} maxWidth="70%">
            {nodeImageUrl && (
              <Image
                className="rounded"
                boxSize="2rem"
                p={1.5}
                bg={categoryIcons[nodeCategory].theme}
                alt={nodeDisplayName}
                draggable={false}
                src={nodeImageUrl || "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Globe_icon-white.svg/2048px-Globe_icon-white.svg.png"}
                flexShrink={0}
              />
            )}
            <Text 
              className="font-semibold text-gray-800 tracking-tight"
              fontSize="16px"
              lineHeight="110%"
              letterSpacing="-4%"
              textTransform="uppercase"
              sx={{
                wordBreak: "break-word",
                hyphens: "auto"
              }}
            >
              {nodeDisplayNameUpper}
            </Text>
          </Flex>
          <Text 
            className="text-gray-800 text-sm whitespace-nowrap"
            flexShrink={0}
            minWidth="140px"
            textAlign="right"
          >
            {formatFullDate(runStartedAt)}
          </Text>
        </Flex>
        
        {/* Description */}
        {triggeredByUserDisplayName && (
          <Box sx={{ paddingY: "6px" }}>
            <Text className="text-sm text-gray-800 mt-1">
              Triggered by: {triggeredByUserEmail !== triggeredByUserDisplayName ? (
                <Tooltip 
                  label={triggeredByUserEmail}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                >
                  <Text as="span" className="cursor-default">{triggeredByUserDisplayName}</Text>
                </Tooltip>
              ) : triggeredByUserDisplayName}
            </Text>
          </Box>
        )}
        
        {/* Footer with status, elapsed time and cost */}
        <Flex sx={{ paddingY: "6px" }}>
          <Flex gap={4} width="100%">
            {/* Status pill on the left */}
            <Box width="100px">
              {nodeStatus && nodeStatus !== 'Unknown' && (
                <Box className={`rounded-md px-3 py-1 ${statusColors.bg} border ${statusColors.border} inline-block`}>
                  <Text className={`text-xs font-medium ${statusColors.text}`}>
                    {nodeStatusTitle}
                  </Text>
                </Box>
              )}
            </Box>
            
            {/* Metrics fixed width section */}
            <Flex 
              flex={1} 
              justifyContent="flex-start" 
              gap="20px" 
              alignItems="center"
              alignSelf="stretch"
              display="flex"
            >
              {/* Time */}
              <Flex align="center" gap={0.5} minWidth="80px">
                <Flex 
                  className="w-5 h-5 rounded-full items-center justify-center"
                  sx={{
                    backgroundColor: "#404040",
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <Text
                    className="material-symbols-rounded"
                    sx={{
                      color: "white",
                      fontFamily: "Material Symbols Rounded",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "100%",
                      letterSpacing: "-0.32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      transform: "scale(1.6)"
                    }}
                  >
                    schedule
                  </Text>
                </Flex>
                <Text className="text-gray-700 text-sm">
                  {formatDuration(durationSeconds)}
                </Text>
              </Flex>
              
              {/* Cost */}
              {totalCost > -1 && (
                <Flex align="center" gap={0.5} minWidth="80px">
                  <Flex 
                    className="w-5 h-5 rounded-full items-center justify-center"
                    sx={{
                      backgroundColor: "#404040",
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Text
                      className="material-symbols-rounded"
                      sx={{
                        color: "white",
                        fontFamily: "Material Symbols Rounded",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "100%",
                        letterSpacing: "-0.32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        transform: "scale(1.6)"
                      }}
                    >
                      paid
                    </Text>
                  </Flex>
                  <Text className="text-gray-700 text-sm">
                    ${totalCost.toFixed(2)}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  return (
    <Box 
      className="w-full h-full overflow-auto" 
      sx={{ borderTop: "1px solid #ECECEC" }}
      ref={logsContainerRef}
      onScroll={handleScroll}
    >
      {/* Tabs and Clear Logs Button */}
      {/* <Flex p={3} borderBottom="1px solid #ECECEC" justifyContent="space-between" alignItems="center">
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            className="!bg-gray-100 !border-gray-200 !text-gray-800 !px-4 !rounded-md"
            fontSize="sm"
            isActive={true}
          >
            All
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="!border-gray-200 !text-gray-600 !px-4 !rounded-md"
            fontSize="sm"
          >
            Ongoing
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="!border-gray-200 !text-gray-600 !px-4 !rounded-md"
            fontSize="sm"
          >
            Completed
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="!border-gray-200 !text-gray-600 !px-4 !rounded-md"
            fontSize="sm"
          >
            Errors
          </Button>
        </Flex>
        
        <Button
          size="sm"
          variant="outline"
          className="!border-red-200 !text-red-600 !px-4 !rounded-md"
          fontSize="sm"
          leftIcon={<MdClear />}
          onClick={handleClearLogs}
          isLoading={isClearingLogs}
          isDisabled={hasBlockers}
          _hover={{ bg: "red.50" }}
        >
          Clear Logs
        </Button>
      </Flex> */}
      
      {events.map((event, index) => {
        if (!event) {
          return null;
        }
        
        const isFixingThisEvent = loadingEventName === event.eventName;
        
        return (
          <Box
            key={index}
            className={clsx(
              event.eventType === EventType.INFORMATIONAL && "bg-white",
              event.eventType === EventType.ACTIONABLE_WARNING && "bg-yellow-50",
              event.eventType === EventType.ACTIONABLE_BLOCKER && "bg-red-50"
            )}
            sx={{
              borderBottom: "1px solid #ECECEC",
              borderRight: "1px solid #ECECEC",
              width: "100%",
              boxSizing: "border-box",
              position: "relative",
              padding: "12px 20px",
            }}
          >
            {event.eventType === EventType.INFORMATIONAL ? (
              renderInformationalEvent(event)
            ) : (
              <Flex direction="column" gap={3} className="p-3 pb-6">
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={3}>
                    <MdWarningAmber 
                      size={20} 
                      className={clsx(
                        event.eventType === EventType.ACTIONABLE_WARNING && "text-yellow-600",
                        event.eventType === EventType.ACTIONABLE_BLOCKER && "text-red-600"
                      )} 
                    />
                    <Text 
                      className={clsx(
                        "font-semibold text-gray-800 tracking-tight",
                        event.eventType === EventType.ACTIONABLE_WARNING && "text-yellow-700",
                        event.eventType === EventType.ACTIONABLE_BLOCKER && "text-red-700"
                      )}
                      fontSize="16px"
                      lineHeight="100%"
                      letterSpacing="-4%"
                    >
                      {snakeCaseToCapital(event.eventName || '')}
                    </Text>
                  </Flex>
                  <Text className="text-gray-800 text-sm whitespace-nowrap">
                    {formatFullDate(event.createdOn || Date.now())}
                  </Text>
                </Flex>
                
                <Text className="text-sm text-gray-800 pl-10">
                  {event.eventMessage || 'No message available'}
                </Text>

                <Flex justify="flex-end" className="mt-2 pl-10">
                  <Button
                    size="sm"
                    onClick={() => handleFixEvent(event)}
                    leftIcon={<MdWarningAmber />}
                    isLoading={isFixingThisEvent}
                    className={clsx(
                      "!px-4 !py-1 !h-8 !text-sm !font-medium rounded-md",
                      event.eventType === EventType.ACTIONABLE_WARNING && 
                        "!bg-yellow-100 !text-yellow-700 hover:!bg-yellow-200 !border !border-yellow-300",
                      event.eventType === EventType.ACTIONABLE_BLOCKER && 
                        "!bg-red-100 !text-red-700 hover:!bg-red-200 !border !border-red-300"
                    )}
                  >
                    Fix Issue
                  </Button>
                </Flex>
              </Flex>
            )}
          </Box>
        );
      })}
      
      {/* Pagination loader */}
      {renderPaginationLoader()}
    </Box>
  );
};
